import React, { useState, useEffect, useCallback } from 'react';
import authedFetch from '../../lib/authedFetch';
import secureStorage from '../../lib/secureStorage';
import logger from '../../lib/logger';
import { toast } from 'sonner';
import {
  Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription,
} from '../ui/sheet';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../ui/tabs';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import {
  User as UserIcon, Building2, Mail, Key, Edit, LogOut, Eye,
  ShieldCheck, Loader2, AlertTriangle, Activity, Clock, Globe, Monitor,
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * Convert an ISO-3166-alpha-2 code into a flag emoji. Uses
 * Regional Indicator codepoints (U+1F1E6..U+1F1FF). Returns the
 * raw code if the input isn't a valid 2-letter code.
 */
const flagEmoji = (code) => {
  if (!code || typeof code !== 'string' || code.length !== 2) return '';
  try {
    return String.fromCodePoint(
      ...[...code.toUpperCase()].map((c) => 0x1f1e6 + c.charCodeAt(0) - 65),
    );
  } catch {
    return '';
  }
};

/**
 * Tiny user-agent parser — just enough to render
 * "Chrome on Mac" / "Safari on iOS" without pulling in a
 * 50KB UA library. Falls back to the raw UA string truncated.
 */
const parseUA = (ua) => {
  if (!ua) return { browser: 'Unknown', os: 'Unknown' };
  const u = ua.toLowerCase();
  let browser = 'Unknown';
  if (u.includes('edg/')) browser = 'Edge';
  else if (u.includes('chrome/') && !u.includes('chromium')) browser = 'Chrome';
  else if (u.includes('firefox/')) browser = 'Firefox';
  else if (u.includes('safari/') && !u.includes('chrome')) browser = 'Safari';
  else if (u.includes('curl/')) browser = 'curl';
  else if (u.includes('python')) browser = 'Python';
  else if (u.includes('postman')) browser = 'Postman';
  let os = 'Unknown';
  if (u.includes('windows')) os = 'Windows';
  else if (u.includes('mac os') || u.includes('macintosh')) os = 'macOS';
  else if (u.includes('iphone') || u.includes('ipad')) os = 'iOS';
  else if (u.includes('android')) os = 'Android';
  else if (u.includes('linux')) os = 'Linux';
  return { browser, os };
};

/**
 * Format an ISO timestamp into "Nm ago" / "Nh ago" / "Nd ago".
 * Falls back to a short locale string for older entries.
 */
const relTime = (iso) => {
  if (!iso) return '—';
  const t = new Date(iso).getTime();
  if (Number.isNaN(t)) return '—';
  const diff = Date.now() - t;
  if (diff < 60_000) return 'just now';
  if (diff < 3_600_000) return `${Math.floor(diff / 60_000)}m ago`;
  if (diff < 86_400_000) return `${Math.floor(diff / 3_600_000)}h ago`;
  if (diff < 30 * 86_400_000) return `${Math.floor(diff / 86_400_000)}d ago`;
  return new Date(iso).toLocaleDateString();
};

const StatTile = ({ label, value, hint, testId }) => (
  <div className="p-3 bg-white border rounded-lg" data-testid={testId}>
    <p className="text-[10px] font-medium text-gray-400 uppercase tracking-wide">{label}</p>
    <p className="text-sm mt-1 font-medium text-gray-900 break-words">{value || '—'}</p>
    {hint && <p className="text-xs text-gray-500 mt-0.5">{hint}</p>}
  </div>
);

export default function UserDetailSlideOver({
  viewingUser,
  onClose,
  organizations,
  roleColors,
  roleNames,
  currentUser,
  authIsSuperAdmin,
  onEdit,
  onChangeRole,
  onResendInvite,
  onImpersonate,
  onUserChanged,
  onForceSignedOut,
}) {
  const [detail, setDetail] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState('profile');
  const [signingOut, setSigningOut] = useState(false);

  const userId = viewingUser?.user_id;

  const fetchDetail = useCallback(async () => {
    if (!userId) return;
    setLoading(true);
    setError(null);
    try {
      const token = secureStorage.getItem('session_token');
      const r = await authedFetch(`${API_URL}/api/rbac/users/${userId}/detail`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      const data = await r.json();
      setDetail(data);
    } catch (e) {
      logger.error('UserDetailSlideOver fetch failed', e);
      setError('Could not load user detail.');
    } finally {
      setLoading(false);
    }
  }, [userId]);

  useEffect(() => {
    if (viewingUser) {
      setActiveTab('profile');
      fetchDetail();
    } else {
      setDetail(null);
      setError(null);
    }
  }, [viewingUser, fetchDetail]);

  const handleForceSignOut = async () => {
    if (!userId) return;
    if (!window.confirm(
      `Sign ${viewingUser.email} out of every active session? They will need to log in again on every device.`,
    )) return;
    setSigningOut(true);
    try {
      const token = secureStorage.getItem('session_token');
      const r = await authedFetch(`${API_URL}/api/rbac/users/${userId}/force-signout`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await r.json();
      if (!r.ok) throw new Error(data?.detail || 'Failed');
      toast.success(`Revoked ${data.sessions_revoked} session${data.sessions_revoked === 1 ? '' : 's'}`);
      await fetchDetail();
      if (onUserChanged) onUserChanged();
      // Close the slide-over and let the parent pulse-highlight the
      // row so the admin sees confirmation in the list view.
      if (onForceSignedOut) onForceSignedOut(userId);
    } catch (e) {
      toast.error(e.message || 'Sign-out failed');
    } finally {
      setSigningOut(false);
    }
  };

  if (!viewingUser) return null;

  const primaryRole = viewingUser.roles?.[0]?.role || 'user';
  const roleConfig = roleColors[primaryRole] || roleColors.user;
  const RoleIcon = roleConfig.icon;
  const pendingInvite = !!detail?.user?.must_change_password;
  const canImpersonate = authIsSuperAdmin && viewingUser.user_id !== currentUser?.user_id;
  const canForceSignOut = (authIsSuperAdmin || currentUser?.permissions?.includes('users.update.org'))
    && viewingUser.user_id !== currentUser?.user_id;

  return (
    <Sheet open={!!viewingUser} onOpenChange={(open) => !open && onClose()}>
      <SheetContent
        side="right"
        className="w-full sm:max-w-2xl flex flex-col overflow-hidden p-0"
        data-testid="user-detail-slideover"
      >
        {/* Header band */}
        <div className="px-6 pt-6 pb-4 border-b bg-slate-50">
          <SheetHeader>
            <div className="flex items-center gap-4">
              <div className={`w-14 h-14 rounded-full flex items-center justify-center ${roleConfig.bg} flex-shrink-0`}>
                <RoleIcon size={28} className={roleConfig.text} />
              </div>
              <div className="min-w-0 flex-1">
                <SheetTitle className="truncate" data-testid="slideover-user-name">
                  {viewingUser.name || 'Unnamed User'}
                </SheetTitle>
                <SheetDescription className="truncate flex items-center gap-2">
                  <Mail size={12} />
                  {viewingUser.email}
                </SheetDescription>
                <div className="flex items-center gap-2 mt-2 flex-wrap">
                  <Badge className={`${roleConfig.bg} ${roleConfig.text}`}>
                    {roleNames[primaryRole] || primaryRole}
                  </Badge>
                  {pendingInvite && (
                    <Badge variant="outline" className="border-amber-300 bg-amber-50 text-amber-700"
                      data-testid="slideover-pending-invite-badge">
                      <Mail size={10} className="mr-1" /> Pending invite
                    </Badge>
                  )}
                  {detail?.active_session_count > 0 && (
                    <Badge variant="outline" className="border-emerald-300 bg-emerald-50 text-emerald-700"
                      data-testid="slideover-session-count">
                      <Activity size={10} className="mr-1" />
                      {detail.active_session_count} active session{detail.active_session_count === 1 ? '' : 's'}
                    </Badge>
                  )}
                  {detail?.unusual_active_count > 0 && (
                    <Badge variant="outline" className="border-amber-400 bg-amber-50 text-amber-800"
                      data-testid="slideover-unusual-active-count">
                      <AlertTriangle size={10} className="mr-1" />
                      {detail.unusual_active_count} unusual location{detail.unusual_active_count === 1 ? '' : 's'}
                    </Badge>
                  )}
                </div>
              </div>
            </div>
          </SheetHeader>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto px-6 py-4">
          {loading && (
            <div className="flex items-center justify-center py-12 text-gray-500">
              <Loader2 size={20} className="animate-spin mr-2" /> Loading user detail…
            </div>
          )}
          {error && !loading && (
            <div className="flex items-center gap-2 p-3 bg-rose-50 border border-rose-200 rounded text-rose-700 text-sm"
              data-testid="slideover-error">
              <AlertTriangle size={16} /> {error}
            </div>
          )}
          {!loading && !error && detail && (
            <Tabs value={activeTab} onValueChange={setActiveTab}>
              <TabsList className="grid grid-cols-4 mb-4">
                <TabsTrigger value="profile" data-testid="slideover-tab-profile">Profile</TabsTrigger>
                <TabsTrigger value="sessions" data-testid="slideover-tab-sessions">
                  Sessions ({detail.active_session_count})
                </TabsTrigger>
                <TabsTrigger value="audit" data-testid="slideover-tab-audit">
                  Audit ({(detail.actor_audit_logs?.length || 0) + (detail.target_audit_logs?.length || 0)})
                </TabsTrigger>
                <TabsTrigger value="logins" data-testid="slideover-tab-logins">
                  Logins ({detail.failed_logins?.length || 0})
                </TabsTrigger>
              </TabsList>

              <TabsContent value="profile" className="space-y-4">
                <div className="grid grid-cols-2 gap-3">
                  <StatTile label="User ID" value={detail.user.user_id}
                    testId="slideover-stat-user-id" />
                  <StatTile label="Auth Provider" value={(detail.user.auth_provider || 'local').toUpperCase()}
                    testId="slideover-stat-auth-provider" />
                  <StatTile label="Created"
                    value={detail.user.created_at ? new Date(detail.user.created_at).toLocaleString() : '—'}
                    hint={detail.user.created_at ? relTime(detail.user.created_at) : null}
                    testId="slideover-stat-created" />
                  <StatTile label="Last Login"
                    value={detail.user.last_login ? new Date(detail.user.last_login).toLocaleString() : 'Never'}
                    hint={detail.user.last_login ? relTime(detail.user.last_login) : null}
                    testId="slideover-stat-last-login" />
                  <StatTile label="Status"
                    value={detail.user.is_active === false ? 'Deactivated' : 'Active'}
                    testId="slideover-stat-status" />
                  <StatTile label="Pending Rotation"
                    value={pendingInvite ? 'Yes (must change password)' : 'No'}
                    hint={detail.user.temp_password_expires_at
                      ? `Expires ${relTime(detail.user.temp_password_expires_at)}` : null}
                    testId="slideover-stat-pending" />
                </div>

                <div>
                  <h4 className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2 flex items-center gap-1">
                    <ShieldCheck size={12} /> Roles & Organizations
                  </h4>
                  <div className="space-y-2" data-testid="slideover-roles-list">
                    {detail.roles.length === 0 ? (
                      <p className="text-sm text-gray-500 italic">No active roles</p>
                    ) : detail.roles.map((r) => {
                      const rConfig = roleColors[r.role] || roleColors.user;
                      return (
                        <div key={r.id} className="p-3 bg-white border rounded-lg flex items-center gap-3">
                          <Badge className={`${rConfig.bg} ${rConfig.text}`}>{roleNames[r.role] || r.role}</Badge>
                          <div className="flex-1 min-w-0">
                            <p className="text-sm font-medium text-gray-900 truncate flex items-center gap-1">
                              <Building2 size={12} className="text-gray-400" />
                              {r.organization_name
                                || organizations.find((o) => o.id === r.organization_id)?.name
                                || 'No organization'}
                            </p>
                            <p className="text-xs text-gray-500">
                              Granted {relTime(r.granted_at)}
                              {r.granted_by ? ` by ${r.granted_by}` : ''}
                            </p>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>

                {detail.roles[0]?.permissions?.length > 0 && (
                  <div>
                    <h4 className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">
                      Permissions ({detail.roles[0].permissions.length})
                    </h4>
                    <div className="flex flex-wrap gap-1.5">
                      {detail.roles[0].permissions.map((p) => (
                        <span key={p} className="text-xs px-2 py-0.5 bg-slate-100 text-slate-700 rounded font-mono">
                          {p}
                        </span>
                      ))}
                    </div>
                  </div>
                )}
              </TabsContent>

              <TabsContent value="sessions">
                {detail.sessions.length === 0 ? (
                  <p className="text-sm text-gray-500 italic py-4">No sessions on record.</p>
                ) : (
                  <div className="space-y-2" data-testid="slideover-sessions-list">
                    {detail.sessions.map((s) => {
                      const { browser, os } = parseUA(s.user_agent);
                      const flag = flagEmoji(s.country_code);
                      const locationLabel = s.country_name || s.country_code || (s.ip ? 'Private network' : 'Unknown');
                      return (
                        <div key={s.session_id}
                          data-testid={`slideover-session-${s.session_id}`}
                          data-current={s.is_current ? 'true' : 'false'}
                          data-unusual={s.is_unusual_location ? 'true' : 'false'}
                          className={`p-3 border rounded-lg flex items-start gap-3 ${
                            s.is_current
                              ? 'bg-sky-50 border-sky-300 ring-1 ring-sky-200'
                              : s.is_unusual_location && s.is_active
                              ? 'bg-amber-50 border-amber-300 ring-1 ring-amber-200'
                              : s.is_active
                              ? 'bg-emerald-50 border-emerald-200'
                              : 'bg-gray-50 border-gray-200 opacity-70'
                          }`}>
                          <div className="flex-shrink-0 mt-0.5">
                            {s.is_current ? (
                              <Activity size={16} className="text-sky-600" />
                            ) : s.is_unusual_location && s.is_active ? (
                              <AlertTriangle size={16} className="text-amber-600" />
                            ) : s.is_active ? (
                              <Activity size={16} className="text-emerald-600" />
                            ) : (
                              <Activity size={16} className="text-gray-400" />
                            )}
                          </div>
                          <div className="flex-1 min-w-0 space-y-1">
                            <div className="flex items-center gap-2 flex-wrap">
                              <span className="text-sm font-medium text-gray-900">
                                {s.is_current ? 'This session' : (s.is_active ? 'Active' : 'Expired')}
                              </span>
                              {s.is_current && (
                                <Badge variant="outline" className="border-sky-300 bg-sky-100 text-sky-700 text-[10px] px-1.5 py-0">
                                  YOU
                                </Badge>
                              )}
                              {s.is_unusual_location && (
                                <Badge variant="outline"
                                  className="border-amber-400 bg-amber-100 text-amber-800 text-[10px] px-1.5 py-0"
                                  data-testid={`slideover-session-${s.session_id}-unusual-badge`}
                                  title={detail.usual_country
                                    ? `User normally signs in from ${detail.usual_country}`
                                    : 'Country differs from this user\'s usual sign-in location'}>
                                  <AlertTriangle size={9} className="mr-0.5" />
                                  Unusual location
                                </Badge>
                              )}
                              <span className="text-xs text-gray-500 capitalize">
                                · {s.auth_source}
                              </span>
                            </div>
                            <div className="flex items-center gap-3 text-xs text-gray-600 flex-wrap"
                              data-testid={`slideover-session-${s.session_id}-meta`}>
                              <span className="flex items-center gap-1" title={s.country_name || ''}>
                                {flag && <span className="text-base leading-none">{flag}</span>}
                                <Globe size={11} className="text-gray-400" />
                                {locationLabel}
                              </span>
                              {s.ip && (
                                <span className="font-mono text-gray-500">{s.ip}</span>
                              )}
                              <span className="flex items-center gap-1" title={s.user_agent || ''}>
                                <Monitor size={11} className="text-gray-400" />
                                {browser} on {os}
                              </span>
                            </div>
                            <p className="text-xs text-gray-500">
                              Last active {relTime(s.last_active_at)} · Created {relTime(s.created_at)} · Expires {relTime(s.expires_at)}
                            </p>
                          </div>
                          <span className="text-xs font-mono text-gray-400 flex-shrink-0" title={s.session_id}>
                            {s.session_id.slice(0, 8)}…
                          </span>
                        </div>
                      );
                    })}
                  </div>
                )}
              </TabsContent>

              <TabsContent value="audit" className="space-y-4">
                <div>
                  <h4 className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2 flex items-center gap-1">
                    <UserIcon size={12} /> Actions taken BY this user ({detail.actor_audit_logs.length})
                  </h4>
                  {detail.actor_audit_logs.length === 0 ? (
                    <p className="text-sm text-gray-500 italic">No actions logged.</p>
                  ) : (
                    <div className="space-y-1" data-testid="slideover-actor-audit">
                      {detail.actor_audit_logs.slice(0, 15).map((log) => (
                        <div key={log.id} className="text-xs p-2 bg-white border rounded flex items-center gap-2">
                          <Clock size={11} className="text-gray-400 flex-shrink-0" />
                          <span className="font-mono text-slate-700 flex-shrink-0">{log.action}</span>
                          <span className="text-gray-500 truncate flex-1">
                            {log.target_name || log.target_id || ''}
                          </span>
                          <span className="text-gray-400 flex-shrink-0">{relTime(log.created_at)}</span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                <div>
                  <h4 className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2 flex items-center gap-1">
                    <ShieldCheck size={12} /> Admin actions taken ON this user ({detail.target_audit_logs.length})
                  </h4>
                  {detail.target_audit_logs.length === 0 ? (
                    <p className="text-sm text-gray-500 italic">No admin actions logged.</p>
                  ) : (
                    <div className="space-y-1" data-testid="slideover-target-audit">
                      {detail.target_audit_logs.slice(0, 15).map((log) => (
                        <div key={log.id} className="text-xs p-2 bg-white border rounded flex items-center gap-2">
                          <Clock size={11} className="text-gray-400 flex-shrink-0" />
                          <span className="font-mono text-slate-700 flex-shrink-0">{log.action}</span>
                          <span className="text-gray-500 truncate flex-1">
                            by {log.actor_email || log.actor_id || 'system'}
                          </span>
                          <span className="text-gray-400 flex-shrink-0">{relTime(log.created_at)}</span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </TabsContent>

              <TabsContent value="logins">
                {detail.failed_logins.length === 0 ? (
                  <div className="p-4 bg-emerald-50 border border-emerald-200 rounded text-sm text-emerald-700">
                    Clean record — no failed login attempts on this account.
                  </div>
                ) : (
                  <div className="space-y-1" data-testid="slideover-failed-logins">
                    <p className="text-xs text-gray-500 mb-2">
                      Most recent {detail.failed_logins.length} failed/blocked login attempts.
                    </p>
                    {detail.failed_logins.map((log) => (
                      <div key={log.id} className="text-xs p-2 bg-rose-50 border border-rose-200 rounded flex items-center gap-2">
                        <AlertTriangle size={11} className="text-rose-500 flex-shrink-0" />
                        <span className="font-mono text-rose-700 flex-shrink-0">{log.event}</span>
                        <span className="text-rose-600 truncate flex-1">
                          {log.reason || ''}{log.ip ? ` · ${log.ip}` : ''}
                          {log.country_code ? ` · ${log.country_code}` : ''}
                        </span>
                        <span className="text-gray-500 flex-shrink-0">{relTime(log.ts)}</span>
                      </div>
                    ))}
                  </div>
                )}
              </TabsContent>
            </Tabs>
          )}
        </div>

        {/* Footer actions */}
        <div className="border-t bg-white px-6 py-3 flex items-center gap-2 flex-wrap">
          <Button variant="outline" size="sm" onClick={() => onEdit(viewingUser)}
            data-testid="slideover-action-edit">
            <Edit size={14} className="mr-1.5" /> Edit
          </Button>
          <Button variant="outline" size="sm" onClick={() => onChangeRole(viewingUser)}
            data-testid="slideover-action-change-role">
            <Key size={14} className="mr-1.5" /> Change Role
          </Button>
          {pendingInvite && onResendInvite && (
            <Button variant="outline" size="sm" onClick={() => onResendInvite(viewingUser)}
              data-testid="slideover-action-resend-invite"
              className="text-teal-700 border-teal-300">
              <Mail size={14} className="mr-1.5" /> Resend Invite
            </Button>
          )}
          {canForceSignOut && (
            <Button variant="outline" size="sm" onClick={handleForceSignOut}
              disabled={signingOut || (detail?.active_session_count || 0) === 0}
              data-testid="slideover-action-force-signout"
              className="text-rose-700 border-rose-300 hover:bg-rose-50">
              {signingOut
                ? <Loader2 size={14} className="mr-1.5 animate-spin" />
                : <LogOut size={14} className="mr-1.5" />}
              Sign out everywhere
            </Button>
          )}
          {canImpersonate && onImpersonate && (
            <Button variant="outline" size="sm" onClick={() => onImpersonate(viewingUser)}
              data-testid="slideover-action-impersonate"
              className="text-amber-700 border-amber-300 hover:bg-amber-50 ml-auto">
              <Eye size={14} className="mr-1.5" /> Impersonate
            </Button>
          )}
        </div>
      </SheetContent>
    </Sheet>
  );
}
